import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from '@parcel/ui';
import type { Column } from '@tanstack/react-table';
import { ArrowUpDown, Info } from 'lucide-react';

interface DataTableColumnHeaderProps<TData, TValue> {
  column: Column<TData, TValue>;
  title: string;
  className?: string;
  tooltip?: string;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
  tooltip,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const canSort = typeof column.getCanSort === 'function' ? column.getCanSort() : false;
  return (
    <Button
      variant="ghost"
      size="sm"
      // -ml-2 cancels the button's own padding (px-2) against the cell's
      // padding (px-3) so the label aligns with cell content while the hover
      // pill keeps breathing room from the column divider.
      // max-w gives back the -ml-2 so the label truncates while the info and sort icons stay visible.
      className={cn(
        '-ml-2 h-8 min-w-0 max-w-[calc(100%+0.5rem)] gap-1.5 px-2 text-xs uppercase text-muted-foreground',
        !canSort && 'cursor-default hover:bg-transparent',
        className,
      )}
      onClick={canSort ? () => column.toggleSorting(column.getIsSorted() === 'asc') : undefined}
    >
      <span className="min-w-0 truncate">{title}</span>
      {tooltip ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground">
                <Info className="h-3 w-3" />
                <span className="sr-only">{title} description</span>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-64 normal-case leading-relaxed">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
      {canSort ? <ArrowUpDown className="h-3.5 w-3.5 shrink-0" /> : null}
    </Button>
  );
}
